import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Store, MapPin, Trash2, Clock, DollarSign, CreditCard, CheckCircle2 } from "lucide-react";
import SmsConsent from "./SmsConsent";
import ZelleInstructions from "./ZelleInstructions";
import { useCustomerAuth } from "@/context/CustomerAuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const money = (v) => `$${Number(v || 0).toFixed(2)}`;

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[10px] font-tactical uppercase tracking-widest text-zinc-600 mb-1 block">{label}</span>
      {children}
    </label>
  );
}

function CheckList({ items, value, onChange, testPrefix, render }) {
  const toggle = (id) => onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
      {items.map((it) => {
        const id = typeof it === "string" ? it : it.id;
        const on = value.includes(id);
        return (
          <label key={id} data-testid={`${testPrefix}-${id.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
            className={`flex items-start gap-2 p-2.5 rounded-md border-2 cursor-pointer transition-colors ${on ? "border-[#0C2E2B] bg-[#FFDF00]/20" : "border-zinc-200 bg-white hover:border-zinc-400"}`}>
            <Checkbox checked={on} onCheckedChange={() => toggle(id)} className="mt-0.5" />
            <span className="text-sm text-zinc-800 leading-tight">{render ? render(it) : it}</span>
          </label>
        );
      })}
    </div>
  );
}

export default function FurnitureDepotQuote() {
  const [cfg, setCfg] = useState(null);
  const [storeId, setStoreId] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "", zip_code: "", preferred_date: "", preferred_window: "8am - 12pm", item_details: "", sms_consent: false });
  const [purchased, setPurchased] = useState([]);
  const [disposal, setDisposal] = useState("no");
  const [disposalItems, setDisposalItems] = useState([]);
  const [quote, setQuote] = useState(null);
  const [method, setMethod] = useState("zelle");
  const [agree, setAgree] = useState(false);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);
  const { user: customer } = useCustomerAuth();
  const [params] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/furniture-depot/config`).then(({ data }) => {
      setCfg(data);
      const pre = params.get("store");
      if (pre && data.stores.some((s) => s.id === pre)) setStoreId(pre);
    }).catch(() => toast.error("Could not load store list"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (customer) setForm((f) => ({ ...f, name: f.name || customer.name || "", email: f.email || customer.email || "" })); }, [customer]);

  const store = useMemo(() => cfg?.stores.find((s) => s.id === storeId), [cfg, storeId]);
  const zipOk = /^\d{5}$/.test(form.zip_code);

  useEffect(() => {
    if (!storeId) { setQuote(null); return; }
    const t = setTimeout(() => {
      axios.post(`${API}/furniture-depot/quote`, { store_id: storeId, zip_code: zipOk ? form.zip_code : "", purchased_items: purchased, disposal_needed: disposal === "yes", disposal_items: disposalItems })
        .then(({ data }) => setQuote(data)).catch(() => {});
    }, 250);
    return () => clearTimeout(t);
  }, [storeId, form.zip_code, zipOk, purchased, disposal, disposalItems]);

  const submit = async (e) => {
    e.preventDefault();
    if (!storeId) return toast.error("Pick your furniture store first");
    if (purchased.length === 0) return toast.error("Select at least one item you purchased");
    if (!agree) return toast.error("Please accept the delivery, assembly & disposal terms");
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/furniture-depot/bookings`, {
        store_id: storeId, ...form, purchased_items: purchased, disposal_needed: disposal === "yes", disposal_items: disposalItems,
        payment_method: method, origin_url: window.location.origin,
      });
      if (method === "card" && data.checkout_url) { window.location.href = data.checkout_url; return; }
      setDone(data);
      toast.success(`Booked ${data.ref_id} — send ${money(data.deposit_amount)} by Zelle to lock it in`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not save your booking. Try again.");
    } finally { setBusy(false); }
  };

  if (done) {
    return (
      <div className="bg-white rounded-2xl border-2 border-[#0C2E2B] p-6 shadow-[8px_8px_0_#0C2E2B]" data-testid="fd-success">
        <div className="flex items-center gap-2 text-emerald-700 font-black text-lg"><CheckCircle2 className="w-6 h-6" /> You're booked · {done.ref_id}</div>
        <p className="text-sm text-zinc-600 mt-1">{done.furniture_depot?.store?.name} · {done.preferred_date} · {done.preferred_window}</p>
        <div className="mt-4"><ZelleInstructions zelle={done.zelle} refId={done.ref_id} purpose="booking" cancellationPolicy={done.cancellation_policy} /></div>
        <p className="text-[11px] text-zinc-500 mt-3 text-center">Total {money(done.deposit_amount)} is due now. Crew will call 30 min before arrival.</p>
      </div>
    );
  }

  if (!cfg) return <div className="text-sm text-zinc-500" data-testid="fd-loading">Loading stores…</div>;

  return (
    <form onSubmit={submit} className="grid lg:grid-cols-[1fr_360px] gap-6 items-start" data-testid="fd-quote-form">
      <div className="space-y-6">
        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 space-y-4">
          <div className="flex items-center gap-2 font-display font-black text-lg text-[#0C2E2B]"><Store className="w-5 h-5" /> 1. Where did you buy?</div>
          <Field label="Furniture store">
            <select data-testid="fd-store-select" required value={storeId} onChange={(e) => setStoreId(e.target.value)} className="w-full rounded-md border border-zinc-300 px-3 py-2.5 text-sm bg-white font-bold">
              <option value="">Choose your store…</option>
              {cfg.stores.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </Field>
          {store && (
            <div data-testid="fd-flat-rate-msg" className="rounded-md bg-[#0C2E2B] text-white px-4 py-3 text-sm">
              <span className="font-tactical text-[10px] uppercase tracking-widest text-[#FFDF00] block">Flat rate</span>
              <b className="font-mono-data text-xl">{money(store.flat_rate)}</b> delivery &amp; assembly within {cfg.included_miles} miles of {store.name} (up to {cfg.included_hours} hrs).
              <span className="block text-white/70 text-xs mt-1">{store.address}</span>
            </div>
          )}
        </section>

        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 space-y-3">
          <div className="flex items-center gap-2 font-display font-black text-lg text-[#0C2E2B]"><MapPin className="w-5 h-5" /> 2. Your info &amp; delivery address</div>
          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="Full name"><Input data-testid="fd-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
            <Field label="Phone"><Input data-testid="fd-phone-input" required type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></Field>
            <Field label="Email (for your receipt)"><Input data-testid="fd-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></Field>
            <Field label="Delivery ZIP"><Input data-testid="fd-zip-input" required inputMode="numeric" maxLength={5} value={form.zip_code} onChange={(e) => setForm({ ...form, zip_code: e.target.value.replace(/\D/g, "").slice(0, 5) })} /></Field>
            <div className="sm:col-span-2"><Field label="Delivery address"><Input data-testid="fd-address-input" required value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Street, city" /></Field></div>
            <Field label="Preferred date"><Input data-testid="fd-date-input" required type="date" value={form.preferred_date} onChange={(e) => setForm({ ...form, preferred_date: e.target.value })} /></Field>
            <Field label="Arrival window">
              <select data-testid="fd-window-select" value={form.preferred_window} onChange={(e) => setForm({ ...form, preferred_window: e.target.value })} className="w-full rounded-md border border-zinc-300 px-3 py-2 text-sm bg-white">
                <option>8am - 12pm</option><option>12pm - 4pm</option><option>4pm - 8pm</option>
              </select>
            </Field>
          </div>
          {store && zipOk && quote?.distance?.known && quote.distance.extra_miles > 0 && (
            <p data-testid="fd-mileage-warning" className="text-xs font-bold text-amber-800 bg-amber-50 border-l-4 border-amber-400 px-3 py-2 rounded-r">
              Heads up — your ZIP is about {quote.distance.miles} miles from {store.name}. Addresses beyond {cfg.included_miles} miles add {money(cfg.per_mile)} per mile ({quote.distance.extra_miles} extra miles → +{money(quote.distance.extra_miles * cfg.per_mile)}).
            </p>
          )}
          {store && zipOk && quote?.zip_unknown && <p className="text-xs text-zinc-500">We couldn't place that ZIP — mileage (if any) will be confirmed by phone.</p>}
        </section>

        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 space-y-3">
          <div className="font-display font-black text-lg text-[#0C2E2B]">3. What did you purchase?</div>
          <CheckList items={cfg.purchase_items} value={purchased} onChange={setPurchased} testPrefix="fd-item" />
          <Field label="Details of the items (sizes, pieces, stairs, anything we should know)">
            <Textarea data-testid="fd-details-input" rows={3} value={form.item_details} onChange={(e) => setForm({ ...form, item_details: e.target.value })} placeholder='e.g. "Queen mattress and box spring with headboard and footboard — 2nd floor bedroom"' />
          </Field>
        </section>

        <section className="bg-white rounded-2xl border-2 border-zinc-200 p-5 space-y-3">
          <div className="flex items-center gap-2 font-display font-black text-lg text-[#0C2E2B]"><Trash2 className="w-5 h-5" /> 4. Disposal of old items needed?</div>
          <select data-testid="fd-disposal-select" value={disposal} onChange={(e) => { setDisposal(e.target.value); if (e.target.value === "no") setDisposalItems([]); }} className="w-full sm:w-64 rounded-md border border-zinc-300 px-3 py-2.5 text-sm bg-white font-bold">
            <option value="no">No — just deliver &amp; assemble</option>
            <option value="yes">Yes — haul away my old items</option>
          </select>
          {disposal === "yes" && (
            <div data-testid="fd-disposal-list" className="space-y-2">
              <p className="text-xs text-zinc-500">Priced per item: {money(cfg.transport_per_cuft)} per cubic foot of trailer space + that item's disposal fee. Old items are picked up within {cfg.disposal_window_hours} hours of your delivery.</p>
              <CheckList items={cfg.disposal_items} value={disposalItems} onChange={setDisposalItems} testPrefix="fd-dispose"
                render={(d) => <>{d.name} <span className="block text-[11px] text-zinc-500 font-mono-data">{money(d.price)}</span></>} />
            </div>
          )}
        </section>
      </div>

      <aside className="lg:sticky lg:top-28 bg-white rounded-2xl border-2 border-[#0C2E2B] p-5 shadow-[8px_8px_0_#0C2E2B]" data-testid="fd-quote-card">
        <div className="font-tactical text-[10px] uppercase tracking-widest text-zinc-500">Your instant quote</div>
        {!quote ? <p className="text-sm text-zinc-500 mt-2">Choose a store to see your flat rate.</p> : (
          <>
            <ul className="mt-3 space-y-2">
              {quote.lines.map((l, i) => (
                <li key={i} className="text-sm" data-testid={`fd-quote-line-${i}`}>
                  <div className="flex justify-between gap-2"><span className="text-zinc-800">{l.label}</span><span className="font-mono-data font-bold">{money(l.amount)}</span></div>
                  {l.note && <div className="text-[11px] text-zinc-500">{l.note}</div>}
                </li>
              ))}
            </ul>
            <div className="flex justify-between items-baseline border-t-2 border-zinc-200 mt-3 pt-3">
              <span className="font-bold">Total due now</span>
              <span data-testid="fd-quote-total" className="font-display font-black text-3xl text-[#0C2E2B]">{money(quote.total)}</span>
            </div>
            <p className="text-[11px] text-zinc-600 mt-2 flex items-start gap-1" data-testid="fd-overage-note"><Clock className="w-3.5 h-3.5 mt-0.5 shrink-0" /> {quote.overage_note}</p>
            {quote.disposal_note && <p className="text-[11px] text-zinc-600 mt-1">{quote.disposal_note}</p>}
          </>
        )}

        <div className="mt-4 space-y-3">
          <SmsConsent checked={form.sms_consent} onChange={(v) => setForm({ ...form, sms_consent: v })} context="booking" testId="fd-sms-consent" />
          <label className="flex items-start gap-2 text-xs text-zinc-700 cursor-pointer" data-testid="fd-terms-check">
            <Checkbox checked={agree} onCheckedChange={(v) => setAgree(!!v)} className="mt-0.5" />
            <span>I agree to the <a href="#terms" className="font-bold underline">Delivery, Assembly &amp; Disposal terms</a> below, including the {cfg.damage_report_hours}-hour damage reporting window.</span>
          </label>
          <div>
            <div className="text-[10px] font-tactical uppercase tracking-widest text-zinc-600 mb-1.5">Pay in full by</div>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" data-testid="fd-method-zelle-btn" onClick={() => setMethod("zelle")} className={`p-2 rounded-md border-2 text-left ${method === "zelle" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"}`}><DollarSign className="w-3.5 h-3.5 inline" /><span className="font-bold text-xs ml-1">Zelle</span></button>
              <button type="button" data-testid="fd-method-card-btn" onClick={() => setMethod("card")} className={`p-2 rounded-md border-2 text-left ${method === "card" ? "border-[#FFDF00] bg-[#FFDF00]/20" : "border-zinc-200 bg-white"}`}><CreditCard className="w-3.5 h-3.5 inline" /><span className="font-bold text-xs ml-1">Card</span></button>
            </div>
          </div>
          <Button type="submit" data-testid="fd-submit-btn" disabled={busy || !quote} className="btn-kinetic rounded-md w-full py-5 text-xs">{busy ? "Booking…" : `Book & pay ${quote ? money(quote.total) : ""}`}</Button>
        </div>
      </aside>
    </form>
  );
}
